import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useAgentContext } from '../../context/agent';
import { useMutation, useQueryClient } from 'react-query';
import { addWebLinks } from '../../api/agent';
import { FaLink, FaPlus, FaTimes, FaExclamationTriangle } from 'react-icons/fa';

type Props = {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddLinks = ({show, setShow} : Props) => {
    const [webLink, setWebLink] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [invalidLinks, setInvalidLinks] = useState<string[]>([])

    const {agentId} = useParams();
    const {getAgentById} = useAgentContext();
    const agent = getAgentById(agentId || "");

    const handleClose = () => setShow(false);
    // const handleShow = () => setShow(true);

    const queryClient = useQueryClient();
    const { mutateAsync: addLinksFn, isLoading} = useMutation(
        addWebLinks, // mutation function goes here
        {
            // mutationKey: ['Agents', agent?.id],
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['WebLinks', agent?.id]);
                handleClose();
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
            },
        }
    );

    const validateLinks = (links: string[]) => {

        let validLinks: string[] = [];
        let invalidLinks: string[] = [];
        
        const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/;
        links.forEach((link) => {
            link = link.trim();
            if(link) {
                if(urlRegex.test(link)) validLinks.push(link);
                else invalidLinks.push(link);
            }
        });
        return {validLinks, invalidLinks};
    }
    
    const handleAdd = async () => {
        if(!webLink) {
            setError('Link(s) required.');
            return
        }
        if(!agent) {
            setError("Unexpected error. Please reload page and retry.")
            return;
        }

        const links = webLink.split('\n');
        const {validLinks, invalidLinks} = validateLinks(links);
        if(invalidLinks.length > 0) {
            setInvalidLinks(invalidLinks);
            return;
        }
    
        addLinksFn({
            agentId: agent.id,
            orgId: agent.organizationId,
            urls: validLinks
        })
    }

    return (
        <Modal 
            show={show} 
            onHide={handleClose}
            size="lg"
            contentClassName="bg-dark border-secondary"
        >
            <Modal.Header 
                closeButton
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <div className="d-flex align-items-center">
                    <div 
                        className="me-3 d-flex align-items-center justify-content-center"
                        style={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#17a2b8",
                            borderRadius: "8px"
                        }}
                    >
                        <FaLink className="text-white" size={16} />
                    </div>
                    <Modal.Title className="text-light mb-0" style={{ fontSize: "18px", fontWeight: "600" }}>
                        Add Web Links
                    </Modal.Title>
                </div>
            </Modal.Header>
            
            <Modal.Body style={{ backgroundColor: "#1a1a1a" }}>
                <Form>
                    <Form.Group className="mb-4">
                        <Form.Label className="text-light fw-semibold mb-2">
                            Website URLs
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            Enter one URL per line. We'll crawl and extract content from these pages.
                        </Form.Text>
                        <Form.Control
                            id='weblinks'
                            as="textarea" 
                            rows={6}
                            value={webLink}
                            onChange={(e) => setWebLink(e.target.value)}
                            className="bg-dark text-light border-secondary"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff",
                                resize: "vertical"
                            }}
                            placeholder={`https://example.com/page1\nhttps://example.com/page2\nhttps://example.com/page3`}
                        />
                    </Form.Group>
                </Form>
                
                {error && (
                    <Alert 
                        variant="danger" 
                        className="mt-3"
                        style={{ 
                            backgroundColor: "#2d1a1a",
                            borderColor: "#dc3545",
                            color: "#ff6b6b"
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FaExclamationTriangle className="me-2" />
                            {error}
                        </div>
                    </Alert>
                )}
                
                {invalidLinks.length > 0 && (
                    <Alert 
                        variant="danger" 
                        className="mt-3"
                        style={{ 
                            backgroundColor: "#2d1a1a",
                            borderColor: "#dc3545",
                            color: "#ff6b6b"
                        }}
                    >
                        <div className="d-flex align-items-start">
                            <FaExclamationTriangle className="me-2 mt-1" />
                            <div>
                                <h6 className="mb-2">Invalid URLs detected:</h6>
                                <ul className="mb-0 ps-3">
                                    {invalidLinks.map((link, index) => (
                                        <li key={index} className="mb-1">{link}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </Alert>
                )}
            </Modal.Body>
            
            <Modal.Footer 
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose}
                    className="d-flex align-items-center"
                    style={{ borderColor: "#555", color: "#ccc" }}
                >
                    <FaTimes className="me-2" size={14} />
                    Cancel
                </Button>
                <Button 
                    variant="info" 
                    onClick={handleAdd}
                    disabled={isLoading}
                    className="d-flex align-items-center"
                    style={{ 
                        backgroundColor: "#17a2b8",
                        borderColor: "#17a2b8"
                    }}
                >
                    {isLoading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Adding...
                        </>
                    ) : (
                        <>
                            <FaPlus className="me-2" size={14} />
                            Add Links
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddLinks;
